import { Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ColorType } from '../../assets/colors/colors.g';
import { getTheme } from '../../stores/selectors/theme.selector';
import CustomButton from '../react-native-material-wrapper/button.component';

interface DeleteAccountBlockDialogProps {
  open: boolean;
  message: string;
  onClose: () => void;
}

const useStyles = makeStyles(() => ({
  paper: {
    width: '100%',
    maxWidth: '425px',
    margin: '0 auto',
    borderRadius: '16px 16px 0 0',
    background: ColorType.WHITE,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 24px 16px 24px',
    borderBottom: `1px solid ${ColorType.GREY3}`,
  },
  handle: {
    width: 33,
    height: 3,
    borderRadius: 5,
    background: ColorType.GREY4,
    marginBottom: 8,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  title: {
    color: ColorType.NEGATIVE,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
  },
  closeButton: {
    padding: 0,
    color: ColorType.GREY6,
  },
  content: {
    padding: '16px 24px',
  },
  message: {
    color: ColorType.BLACK,
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${ColorType.GREY2}`,
  },
  button: {
    height: 48,
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: 600,
    lineHeight: '24px',
    borderRadius: 4,
    textTransform: 'inherit',
    width: '100%',
    padding: '12px 16px',
    '&:hover': {
      boxShadow: 'none',
    },
  },
}));

export const DeleteAccountBlockDialog = ({
  open,
  message,
  onClose,
}: DeleteAccountBlockDialogProps) => {
  const { i18n } = useTranslation();
  const theme = useSelector(getTheme);
  const classes = useStyles();

  return (
    <Drawer anchor='bottom' open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <div className={classes.header}>
        <div className={classes.handle} />
        <div className={classes.headerRow}>
          <Typography className={classes.title} component='h2'>
            {i18n.t('unable_to_delete_account')}
          </Typography>
          <IconButton
            type='button'
            className={classes.closeButton}
            onClick={onClose}
            aria-label={i18n.t('close')}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <div className={classes.content}>
        <Typography className={classes.message}>{message}</Typography>
      </div>
      <div className={classes.footer}>
        <CustomButton
          type='button'
          variant='contained'
          className={classes.button}
          style={{
            backgroundColor: theme.brand_2,
            color: ColorType.WHITE,
          }}
          onClick={onClose}
          disableElevation
        >
          {i18n.t('got_it_exclamation')}
        </CustomButton>
      </div>
    </Drawer>
  );
};
